import { useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlarmIcon,
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  DotsThreeVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { useIgnoreAPInvoices } from '@/api/rpc/hooks/accounts-payable/useIgnoreAPInvoices';
import { useUnignoreAPInvoices } from '@/api/rpc/hooks/accounts-payable/useUnignoreAPInvoices';
import { useReprocessAPInvoice } from '@/api/rpc/hooks/accounts-payable/useReprocessAPInvoice';
import { useDeleteAPInvoice } from '@/api/rpc/hooks/accounts-payable/useDeleteAPInvoice';
import { useMarkAPInvoicesPaid } from '@/api/rpc/hooks/accounts-payable/useMarkAPInvoicesPaid';
import { MarkInvoicesPaidDialog } from '@/components/accounts-payable/mark-paid/MarkInvoicesPaidDialog';
import { InvoiceExceptionsMenu } from '@/components/accounts-payable/exceptions/InvoiceExceptionsMenu';
import { useInvoiceSnoozeMenu } from '@/components/accounts-payable/snooze/InvoiceSnoozeActions';
import type { MarkAPInvoicesPaidInput } from '@erp/rpc';
import {
  isIgnoredInvoiceStatus,
  singleInvoiceIgnoreDecision,
} from '@/components/accounts-payable/shared/invoice-ignore-filters';
import {
  invoiceMenuIsWaiting,
  invoiceOverflowActions,
  overflowMenuIsEmpty,
  type InvoiceOverflowSubject,
} from '@/components/accounts-payable/invoice-detail/invoiceOverflowActions';
import { useIgnoredInvoiceTasks } from '@/components/accounts-payable/invoice-detail/useIgnoredInvoiceTasks';
import { useOpenInvoiceTasks } from '@/components/accounts-payable/invoice-detail/useOpenInvoiceTasks';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCan } from '@/hooks/useCan';

function UnignoreInvoiceMenu({
  invoiceId,
  invoiceNumber,
  children,
}: {
  invoiceId: string;
  invoiceNumber: string | null | undefined;
  children: (item: ReactNode) => ReactNode;
}) {
  const gt = useGT();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenAssociatedTasks, setReopenAssociatedTasks] = useState(true);
  const { hasClosedForIgnore } = useIgnoredInvoiceTasks(invoiceId);
  const unignoreInvoices = useUnignoreAPInvoices();

  async function confirmUnignore() {
    try {
      const result = await unignoreInvoices.mutateAsync({
        invoiceIds: [invoiceId],
        reopenAssociatedTasks: hasClosedForIgnore
          ? reopenAssociatedTasks
          : true,
      });
      if (result.unignored > 0) setConfirmOpen(false);
    } catch {
      // The mutation hook toasts the error and leaves this dialog open.
    }
  }

  return (
    <>
      {children(
        <DropdownMenuItem
          onClick={() => {
            setReopenAssociatedTasks(true);
            setConfirmOpen(true);
          }}
        >
          <EyeIcon className="mr-1.5 h-4 w-4" />
          <T context="invoice slide-in menu: return this ignored invoice to the working queue">
            Unignore
          </T>
        </DropdownMenuItem>,
      )}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          if (unignoreInvoices.isPending) return;
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          void confirmUnignore();
        }}
        closeOnConfirm={false}
        loading={unignoreInvoices.isPending}
        title={
          invoiceNumber
            ? gt('Unignore invoice {number}', {
                context:
                  'dialog title when unignoring one AP invoice that has a number',
                number: invoiceNumber,
              })
            : gt('Unignore Invoice', {
                context:
                  'dialog title when unignoring one AP invoice with no number',
              })
        }
        confirmText={gt('Unignore', {
          context: 'confirm button, unignore one AP invoice',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button, unignore one AP invoice',
        })}
      >
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            <T context="explanation before returning one ignored AP invoice to its previous status">
              This invoice returns to the status it had before it was ignored.
            </T>
          </p>
          {hasClosedForIgnore && (
            <div className="flex items-start gap-2 text-foreground">
              <Checkbox
                id="reopen-invoice-tasks"
                checked={reopenAssociatedTasks}
                onCheckedChange={(checked) =>
                  setReopenAssociatedTasks(checked === true)
                }
              />
              <Label
                htmlFor="reopen-invoice-tasks"
                className="text-sm font-medium leading-snug"
              >
                <T context="prechecked option when unignoring one AP invoice whose tasks were closed because it was ignored">
                  Reopen tasks that were closed because this invoice was
                  ignored.
                </T>
              </Label>
            </div>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}

function IgnoreInvoiceMenu({
  invoiceId,
  invoiceNumber,
  includeFinalizedUnpaid,
  children,
}: {
  invoiceId: string;
  invoiceNumber: string | null | undefined;
  includeFinalizedUnpaid: boolean;
  children: (item: ReactNode) => ReactNode;
}) {
  const gt = useGT();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closeAssociatedTasks, setCloseAssociatedTasks] = useState(true);
  const { hasOpenTasks } = useOpenInvoiceTasks(invoiceId);
  const ignoreInvoices = useIgnoreAPInvoices();

  async function confirmIgnore() {
    try {
      const result = await ignoreInvoices.mutateAsync({
        invoiceIds: [invoiceId],
        includeFinalizedUnpaid,
        closeAssociatedTasks: hasOpenTasks ? closeAssociatedTasks : true,
      });
      if (result.ignored > 0) setConfirmOpen(false);
    } catch {
      // The mutation hook toasts the error and leaves this dialog open.
    }
  }

  return (
    <>
      {children(
        <DropdownMenuItem
          onClick={() => {
            setCloseAssociatedTasks(true);
            setConfirmOpen(true);
          }}
        >
          <EyeSlashIcon className="mr-1.5 h-4 w-4" />
          <T context="invoice slide-in menu: ignore this invoice">Ignore</T>
        </DropdownMenuItem>,
      )}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          if (ignoreInvoices.isPending) return;
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          void confirmIgnore();
        }}
        closeOnConfirm={false}
        loading={ignoreInvoices.isPending}
        title={
          invoiceNumber
            ? gt('Ignore invoice {number}', {
                context:
                  'dialog title when ignoring one AP invoice that has a number',
                number: invoiceNumber,
              })
            : gt('Ignore Invoice', {
                context:
                  'dialog title when ignoring one AP invoice with no number',
              })
        }
        confirmText={gt('Ignore', {
          context: 'confirm button, ignore one AP invoice',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel button, ignore one AP invoice',
        })}
      >
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            <T context="explanation before ignoring one AP invoice">
              Ignored invoices leave the working queue. They are not deleted.
            </T>
          </p>
          {includeFinalizedUnpaid && (
            <p>
              <T context="warning that this AP invoice is finalized and unpaid before ignoring it">
                This invoice is finalized and unpaid.
              </T>
            </p>
          )}
          {hasOpenTasks && (
            <div className="flex items-start gap-2 text-foreground">
              <Checkbox
                id="close-invoice-tasks"
                checked={closeAssociatedTasks}
                onCheckedChange={(checked) =>
                  setCloseAssociatedTasks(checked === true)
                }
              />
              <Label
                htmlFor="close-invoice-tasks"
                className="text-sm font-medium leading-snug"
              >
                <T context="prechecked option when ignoring one AP invoice that has open tasks">
                  Any tasks associated with this invoice will be closed.
                </T>
              </Label>
            </div>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}

function ReprocessOverflowItem({
  show,
  pending,
  onOpen,
}: {
  show: boolean;
  pending: boolean;
  onOpen: () => void;
}) {
  if (!show) return null;
  return (
    <DropdownMenuItem disabled={pending} onClick={onOpen}>
      <ArrowsClockwiseIcon className="mr-1.5 h-4 w-4" />
      {pending ? (
        <T context="invoice overflow menu: reprocess already running">
          Reprocessing...
        </T>
      ) : (
        <T context="invoice overflow menu: run invoice processing again">
          Reprocess
        </T>
      )}
    </DropdownMenuItem>
  );
}

function MarkPaidOverflowItem({
  show,
  disabled,
  pending,
  onOpen,
}: {
  show: boolean;
  disabled: boolean;
  pending: boolean;
  onOpen: () => void;
}) {
  if (!show) return null;
  return (
    <DropdownMenuItem disabled={disabled} onClick={onOpen}>
      <CheckCircleIcon className="mr-1.5 h-4 w-4" />
      {pending ? (
        <T context="invoice overflow menu: recording the invoice as paid">
          Marking as Paid...
        </T>
      ) : (
        <T context="invoice overflow menu: record this AP invoice as paid outside Gently">
          Mark as Paid
        </T>
      )}
    </DropdownMenuItem>
  );
}

function DeleteOverflowItem({
  show,
  disabled,
  pending,
  onOpen,
}: {
  show: boolean;
  disabled: boolean;
  pending: boolean;
  onOpen: () => void;
}) {
  if (!show) return null;
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        variant="destructive"
        disabled={disabled}
        onClick={onOpen}
      >
        <TrashIcon className="mr-1.5 h-4 w-4" />
        {pending ? (
          <T context="invoice overflow menu: delete in progress">Deleting...</T>
        ) : (
          <T context="invoice overflow menu: permanently delete this invoice">
            Delete Invoice
          </T>
        )}
      </DropdownMenuItem>
    </>
  );
}

function SnoozeMenuItems({
  canSnooze,
  snoozed,
  clearPending,
  onSnooze,
  onClear,
}: {
  canSnooze: boolean;
  snoozed: boolean;
  clearPending: boolean;
  onSnooze: () => void;
  onClear: () => void;
}) {
  return (
    <>
      {canSnooze && (
        <DropdownMenuItem onClick={onSnooze}>
          <AlarmIcon className="mr-1.5 h-4 w-4" />
          <T context="invoice slide-in menu: snooze this draft invoice">
            Snooze
          </T>
        </DropdownMenuItem>
      )}
      {snoozed && (
        <DropdownMenuItem disabled={clearPending} onClick={onClear}>
          <T context="invoice slide-in menu: clear this invoice snooze">
            Clear Snooze
          </T>
        </DropdownMenuItem>
      )}
    </>
  );
}

function overflowMenuProps(input: {
  invoice: { id: string; supplierId?: string | null };
  actionsLabel: string;
  showSnooze: boolean;
  snoozeItem: ReactNode;
  snoozeDialog: ReactNode;
  overflow: ReturnType<typeof invoiceOverflowActions>;
  isReprocessing: boolean;
  setReprocessOpen: (open: boolean) => void;
  markPaid: {
    isPending: boolean;
    mutateAsync: (
      input: MarkAPInvoicesPaidInput,
    ) => Promise<{ markedPaid: number }>;
  };
  setMarkPaidOpen: (open: boolean) => void;
  deleteInvoice: {
    isPending: boolean;
    mutate: (input: { id: string }) => void;
  };
  setDeleteOpen: (open: boolean) => void;
  reprocessOpen: boolean;
  reprocess: () => void;
  markPaidOpen: boolean;
  deleteOpen: boolean;
  gt: ReturnType<typeof useGT>;
}) {
  return {
    loaded: input.invoice,
    actionsLabel: input.actionsLabel,
    showSnooze: input.showSnooze,
    snoozeItem: input.snoozeItem,
    snoozeDialog: input.snoozeDialog,
    overflow: input.overflow,
    isReprocessing: input.isReprocessing,
    setReprocessOpen: input.setReprocessOpen,
    markPaidPending: input.markPaid.isPending,
    setMarkPaidOpen: input.setMarkPaidOpen,
    deletePending: input.deleteInvoice.isPending,
    deleteMutate: input.deleteInvoice.mutate,
    setDeleteOpen: input.setDeleteOpen,
    reprocessOpen: input.reprocessOpen,
    reprocess: input.reprocess,
    markPaidOpen: input.markPaidOpen,
    markPaid: input.markPaid,
    deleteOpen: input.deleteOpen,
    gt: input.gt,
  };
}

function leaveInvoiceAfterDelete(
  navigate: ReturnType<typeof useNavigate>,
  success: boolean,
) {
  if (success) void navigate({ to: '..', resetScroll: false });
}

function OverflowMenuEntries({
  snoozeItem,
  queueItem,
  exceptionsItem,
  loaded,
  overflow,
  isReprocessing,
  setReprocessOpen,
  markPaidPending,
  setMarkPaidOpen,
  deletePending,
  setDeleteOpen,
}: {
  snoozeItem: ReactNode;
  queueItem: ReactNode;
  exceptionsItem: ReactNode;
  loaded: { supplierId?: string | null };
  overflow: ReturnType<typeof invoiceOverflowActions>;
  isReprocessing: boolean;
  setReprocessOpen: (open: boolean) => void;
  markPaidPending: boolean;
  setMarkPaidOpen: (open: boolean) => void;
  deletePending: boolean;
  setDeleteOpen: (open: boolean) => void;
}) {
  return (
    <>
      {snoozeItem}
      {queueItem}
      <ReprocessOverflowItem
        show={overflow.showReprocess}
        pending={isReprocessing}
        onOpen={() => setReprocessOpen(true)}
      />
      <MarkPaidOverflowItem
        show={overflow.showMarkPaid}
        disabled={!loaded.supplierId || markPaidPending}
        pending={markPaidPending}
        onOpen={() => setMarkPaidOpen(true)}
      />
      {exceptionsItem}
      <DeleteOverflowItem
        show={overflow.deleteVisible}
        disabled={!overflow.deleteEnabled || deletePending}
        pending={deletePending}
        onOpen={() => setDeleteOpen(true)}
      />
    </>
  );
}

function OverflowMenuButton({ label }: { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={label}>
            <DotsThreeVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

function InvoiceIgnoreHost({
  ignored,
  invoiceId,
  invoiceNumber,
  canUpdate,
  canIgnore,
  includeFinalizedUnpaid,
  render,
}: {
  ignored: boolean;
  invoiceId: string;
  invoiceNumber: string | null | undefined;
  canUpdate: boolean;
  canIgnore: boolean;
  includeFinalizedUnpaid: boolean;
  render: (queueItem: ReactNode) => ReactNode;
}) {
  if (ignored) {
    return (
      <UnignoreInvoiceMenu invoiceId={invoiceId} invoiceNumber={invoiceNumber}>
        {(item) => render(canUpdate ? item : null)}
      </UnignoreInvoiceMenu>
    );
  }
  return (
    <IgnoreInvoiceMenu
      invoiceId={invoiceId}
      invoiceNumber={invoiceNumber}
      includeFinalizedUnpaid={includeFinalizedUnpaid}
    >
      {(item) => render(canUpdate && canIgnore ? item : null)}
    </IgnoreInvoiceMenu>
  );
}

/**
 * Slide-in overflow menu: snooze, ignore, reprocess, mark paid, delete, and
 * the exceptions submenu. Primary actions (accept, pay, revert) stay on the
 * panel.
 */

function OverflowMenuDialogs({
  loaded,
  overflow,
  isReprocessing,
  setReprocessOpen,
  reprocessOpen,
  reprocess,
  markPaidPending,
  setMarkPaidOpen,
  markPaidOpen,
  markPaid,
  deletePending,
  deleteMutate,
  setDeleteOpen,
  deleteOpen,
  gt,
}: {
  loaded: { id: string };
  overflow: { deleteEnabled: boolean };
  isReprocessing: boolean;
  setReprocessOpen: (open: boolean) => void;
  reprocessOpen: boolean;
  reprocess: () => void;
  markPaidPending: boolean;
  setMarkPaidOpen: (open: boolean) => void;
  markPaidOpen: boolean;
  markPaid: {
    mutateAsync: (
      input: import('@erp/rpc').MarkAPInvoicesPaidInput,
    ) => Promise<{ markedPaid: number }>;
  };
  deletePending: boolean;
  deleteMutate: (input: { id: string }) => void;
  setDeleteOpen: (open: boolean) => void;
  deleteOpen: boolean;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <>
      <ConfirmDialog
        open={reprocessOpen}
        onClose={() => setReprocessOpen(false)}
        onConfirm={() => {
          setReprocessOpen(false);
          reprocess();
        }}
        title={gt('Reprocess Invoice?')}
        message={gt(
          'This runs the invoice processing workflow again from the current invoice: line items are rematched against Famous and the payment is rescheduled. The invoice itself is not recreated.',
        )}
        confirmText={gt('Reprocess')}
        cancelText={gt('Cancel')}
        loading={isReprocessing}
      />
      <MarkInvoicesPaidDialog
        open={markPaidOpen}
        onClose={() => setMarkPaidOpen(false)}
        invoiceIds={[loaded.id]}
        loading={markPaidPending}
        onConfirm={(input) => {
          void markPaid
            .mutateAsync(input)
            .then((result) => {
              if (result.markedPaid > 0) setMarkPaidOpen(false);
            })
            .catch(() => {
              // The mutation hook toasts the error and leaves the dialog open.
            });
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          if (!overflow.deleteEnabled || deletePending) return;
          setDeleteOpen(false);
          deleteMutate({ id: loaded.id });
        }}
        title={gt('Delete Invoice?')}
        message={gt(
          'This will permanently delete the invoice and all associated data. This action cannot be undone.',
        )}
        confirmText={gt('Delete Invoice')}
        cancelText={gt('Cancel')}
        loading={deletePending}
      />
    </>
  );
}

type OverflowMenuProps = {
  showSnooze: boolean;
  snoozeItem: ReactNode;
  snoozeDialog: ReactNode;
  queueItem: ReactNode;
  exceptionsItem: ReactNode;
  loaded: { id: string; supplierId?: string | null };
  actionsLabel: string;
  overflow: ReturnType<typeof invoiceOverflowActions>;
  isReprocessing: boolean;
  setReprocessOpen: (open: boolean) => void;
  markPaidPending: boolean;
  setMarkPaidOpen: (open: boolean) => void;
  deletePending: boolean;
  deleteMutate: (input: { id: string }) => void;
  setDeleteOpen: (open: boolean) => void;
  reprocessOpen: boolean;
  reprocess: () => void;
  markPaidOpen: boolean;
  markPaid: {
    isPending: boolean;
    mutateAsync: (
      input: MarkAPInvoicesPaidInput,
    ) => Promise<{ markedPaid: number }>;
  };
  deleteOpen: boolean;
  gt: ReturnType<typeof useGT>;
};

function renderOverflowMenu({ showSnooze, ...props }: OverflowMenuProps) {
  if (
    overflowMenuIsEmpty({
      queueItem: props.queueItem,
      exceptionsItem: props.exceptionsItem,
      showSnooze,
      showReprocess: props.overflow.showReprocess,
      showMarkPaid: props.overflow.showMarkPaid,
      deleteVisible: props.overflow.deleteVisible,
    })
  ) {
    return null;
  }
  return <OverflowMenuShell {...props} />;
}

function OverflowMenuShell(props: Omit<OverflowMenuProps, 'showSnooze'>) {
  const {
    snoozeItem,
    snoozeDialog,
    queueItem,
    exceptionsItem,
    loaded,
    actionsLabel,
    overflow,
    isReprocessing,
    setReprocessOpen,
    markPaidPending,
    setMarkPaidOpen,
    deletePending,
    deleteMutate,
    setDeleteOpen,
    reprocessOpen,
    reprocess,
    markPaidOpen,
    markPaid,
    deleteOpen,
    gt,
  } = props;
  return (
    <>
      <DropdownMenu>
        <OverflowMenuButton label={actionsLabel} />
        <DropdownMenuContent align="end">
          <OverflowMenuEntries
            snoozeItem={snoozeItem}
            queueItem={queueItem}
            exceptionsItem={exceptionsItem}
            loaded={loaded}
            overflow={overflow}
            isReprocessing={isReprocessing}
            setReprocessOpen={setReprocessOpen}
            markPaidPending={markPaidPending}
            setMarkPaidOpen={setMarkPaidOpen}
            deletePending={deletePending}
            setDeleteOpen={setDeleteOpen}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {snoozeDialog}
      <OverflowMenuDialogs
        loaded={loaded}
        overflow={overflow}
        isReprocessing={isReprocessing}
        setReprocessOpen={setReprocessOpen}
        reprocessOpen={reprocessOpen}
        reprocess={reprocess}
        markPaidPending={markPaidPending}
        setMarkPaidOpen={setMarkPaidOpen}
        markPaidOpen={markPaidOpen}
        markPaid={markPaid}
        deletePending={deletePending}
        deleteMutate={deleteMutate}
        setDeleteOpen={setDeleteOpen}
        deleteOpen={deleteOpen}
        gt={gt}
      />
    </>
  );
}

function useInvoiceMenuPermissions() {
  const [canUpdate, { isLoading: updateLoading }] = useCan(
    Action.Update,
    Subject.APInvoice,
  );
  const [canUpdatePayment, { isLoading: paymentLoading }] = useCan(
    Action.Update,
    Subject.APInvoicePayment,
  );
  const [canDelete, { isLoading: deleteLoading }] = useCan(
    Action.Delete,
    Subject.APInvoice,
  );
  return {
    canUpdate,
    canUpdatePayment,
    canDelete,
    updateLoading,
    paymentLoading,
    deleteLoading,
  };
}

export function InvoiceActionsMenu({ invoiceId }: { invoiceId: string }) {
  const gt = useGT();
  const navigate = useNavigate();
  const { invoice: payload, isLoading } = useAPInvoice(invoiceId);
  const invoice = payload?.invoice;
  const {
    canUpdate,
    canUpdatePayment,
    canDelete,
    updateLoading,
    paymentLoading,
    deleteLoading,
  } = useInvoiceMenuPermissions();
  const { reprocess, isReprocessing } = useReprocessAPInvoice(invoiceId);
  const deleteInvoice = useDeleteAPInvoice({
    onSuccess: (data) => leaveInvoiceAfterDelete(navigate, data.success),
  });
  const markPaid = useMarkAPInvoicesPaid();
  const snooze = useInvoiceSnoozeMenu(invoice ?? null);
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  const actionsLabel = gt('More invoice actions', {
    context:
      'invoice slide-in: tooltip and accessible name for the overflow actions menu',
  });

  return (
    <LoadedInvoiceActionsMenu
      invoiceId={invoiceId}
      invoice={invoice}
      access={{ canUpdate, canUpdatePayment, canDelete }}
      loading={{
        invoice: isLoading,
        update: updateLoading,
        payment: paymentLoading,
        delete: deleteLoading,
      }}
      actionsLabel={actionsLabel}
      snooze={snooze}
      isReprocessing={isReprocessing}
      reprocess={reprocess}
      markPaid={markPaid}
      deleteInvoice={deleteInvoice}
      open={{
        reprocess: reprocessOpen,
        markPaid: markPaidOpen,
        delete: deleteOpen,
      }}
      setReprocessOpen={setReprocessOpen}
      setMarkPaidOpen={setMarkPaidOpen}
      setDeleteOpen={setDeleteOpen}
      gt={gt}
    />
  );
}

type LoadedInvoice = InvoiceOverflowSubject & {
  id: string;
  invoiceNumber?: string | null;
};

type LoadedMenuInput = {
  invoiceId: string;
  invoice: LoadedInvoice | null | undefined;
  access: {
    canUpdate: boolean;
    canUpdatePayment: boolean;
    canDelete: boolean;
  };
  loading: {
    invoice: boolean;
    update: boolean;
    payment: boolean;
    delete: boolean;
  };
  actionsLabel: string;
  snooze: ReturnType<typeof useInvoiceSnoozeMenu>;
  isReprocessing: boolean;
  reprocess: () => void;
  markPaid: Parameters<typeof overflowMenuProps>[0]['markPaid'];
  deleteInvoice: Parameters<typeof overflowMenuProps>[0]['deleteInvoice'];
  open: { reprocess: boolean; markPaid: boolean; delete: boolean };
  setReprocessOpen: (open: boolean) => void;
  setMarkPaidOpen: (open: boolean) => void;
  setDeleteOpen: (open: boolean) => void;
  gt: ReturnType<typeof useGT>;
};

function visibleLoadedInvoice(input: LoadedMenuInput): LoadedInvoice | null {
  const waiting = invoiceMenuIsWaiting({
    invoiceLoading: input.loading.invoice,
    updateLoading: input.loading.update,
    paymentLoading: input.loading.payment,
    deleteLoading: input.loading.delete,
    invoiceMissing: input.invoice == null,
  });
  if (waiting || input.invoice == null) return null;
  return input.invoice;
}

function snoozeOverflowFields(
  canUpdate: boolean,
  snooze: ReturnType<typeof useInvoiceSnoozeMenu>,
): Pick<OverflowMenuProps, 'showSnooze' | 'snoozeItem' | 'snoozeDialog'> {
  const showSnooze = canUpdate && (snooze.canSnooze || snooze.snoozed);
  if (!showSnooze) {
    return { showSnooze: false, snoozeItem: null, snoozeDialog: null };
  }
  return {
    showSnooze: true,
    snoozeItem: (
      <SnoozeMenuItems
        canSnooze={snooze.canSnooze}
        snoozed={snooze.snoozed}
        clearPending={snooze.clearPending}
        onSnooze={snooze.openDialog}
        onClear={snooze.clear}
      />
    ),
    snoozeDialog: snooze.dialog,
  };
}

function LoadedInvoiceActionsMenu(input: LoadedMenuInput) {
  const invoice = visibleLoadedInvoice(input);
  if (!invoice) return null;

  const ignoreDecision = singleInvoiceIgnoreDecision(invoice);
  return (
    <InvoiceActionsMenuFrame
      invoiceId={input.invoiceId}
      ignored={isIgnoredInvoiceStatus(invoice.status)}
      invoiceNumber={invoice.invoiceNumber}
      canUpdate={input.access.canUpdate}
      canIgnore={ignoreDecision.canIgnore}
      includeFinalizedUnpaid={ignoreDecision.includeFinalizedUnpaid}
      menu={overflowMenuProps({
        invoice,
        actionsLabel: input.actionsLabel,
        ...snoozeOverflowFields(input.access.canUpdate, input.snooze),
        overflow: invoiceOverflowActions(invoice, {
          canUpdate: input.access.canUpdate,
          canUpdatePayment: input.access.canUpdatePayment,
          canDelete: input.access.canDelete,
        }),
        isReprocessing: input.isReprocessing,
        setReprocessOpen: input.setReprocessOpen,
        markPaid: input.markPaid,
        setMarkPaidOpen: input.setMarkPaidOpen,
        deleteInvoice: input.deleteInvoice,
        setDeleteOpen: input.setDeleteOpen,
        reprocessOpen: input.open.reprocess,
        reprocess: input.reprocess,
        markPaidOpen: input.open.markPaid,
        deleteOpen: input.open.delete,
        gt: input.gt,
      })}
    />
  );
}

function InvoiceActionsMenuFrame({
  invoiceId,
  ignored,
  invoiceNumber,
  canUpdate,
  canIgnore,
  includeFinalizedUnpaid,
  menu,
}: {
  invoiceId: string;
  ignored: boolean;
  invoiceNumber: string | null | undefined;
  canUpdate: boolean;
  canIgnore: boolean;
  includeFinalizedUnpaid: boolean;
  menu: Omit<
    Parameters<typeof renderOverflowMenu>[0],
    'queueItem' | 'exceptionsItem'
  >;
}) {
  return (
    <InvoiceExceptionsMenu invoiceId={invoiceId}>
      {(exceptionsItem) => (
        <InvoiceIgnoreHost
          ignored={ignored}
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          canUpdate={canUpdate}
          canIgnore={canIgnore}
          includeFinalizedUnpaid={includeFinalizedUnpaid}
          render={(queueItem) =>
            renderOverflowMenu({
              ...menu,
              queueItem,
              exceptionsItem,
            })
          }
        />
      )}
    </InvoiceExceptionsMenu>
  );
}
